import api from "./client";


// Adicione esta função ao arquivo API.tsx
// ✅ ESTA É A FUNÇÃO CERTA PARA USAR:
// dentro de services/API.ts
export interface Ocorrencia {
	ocorrencia_id: number;
	dataHora: string;
	ativo: boolean;
}


export async function criarOcorrencia(payload: {
	usuario_id: number;
	produto_estoque_id: number;
	localizacao_id: number;
	quantidade_esperada: number;
}) {
	try {
		const { data } = await api.post(`/ocorrencia`, {
			usuario_id: payload.usuario_id,
			produto_estoque_id: payload.produto_estoque_id,
			localizacao_id: payload.localizacao_id,
			quantidade_esperada: payload.quantidade_esperada,
		});

		return data; // opcional, caso queira retornar a ocorrência criada
	} catch (err: any) {
		console.error("Erro ao criar ocorrência:", err);
		throw new Error(
			err?.response?.data?.message || "Erro ao registrar ocorrência.",
		);
	}
}


// Ciclo de conferência da ocorrência (abas Pendentes / Aguardando Aprovação /
// Conferidas). Espelha o enum StatusOcorrencia do backend.
export type StatusOcorrencia =
	| "pendente"
	| "aguardando_aprovacao"
	| "conferida";

export interface ProdutoOcorrencia {
	produto_id: number;
	descricao: string;
	sku: string;
	ean: string;
	qtd_sistema: number;
	qtd_esperada: number;
	diferenca: number;
	qtd_ocorrencias: number;
	qtd_ocorrencias_produto: number;
	ocorrencia_id: number;
	responsavel: string | null;
}

export interface GrupoOcorrencia {
	ocorrencia_id: number;
	dataHora: string | null;
	data_termino: string | null;
	armazem: string | null;
	localizacao_id: number;
	localizacao: string | null;
	localizacao_ean: string | null;
	responsavel: string | null;
	qtd_ocorrencias: number;
	status: StatusOcorrencia;
	prioridade: "Alta" | "Media" | "Baixa";
	produtos: ProdutoOcorrencia[];
}

export interface ListarOcorrenciasParams {
	status: StatusOcorrencia;
	search?: string;
	armazem?: string;
	prioridade?: string;
	dataInicio?: string;
	dataFim?: string;
	orderBy?: string;
	orderDirection?: "asc" | "desc";
	offset?: number;
	limit?: number;
}

// Listagem paginada por localização (server-side). Retorna a página de grupos
// já agregada pelo backend + o total de grupos para a paginação.
export async function buscarOcorrencias(
	params: ListarOcorrenciasParams,
): Promise<{ results: GrupoOcorrencia[]; total: number }> {
	try {
		const res = await api.get("/ocorrencia/listar-por-localizacao", {
			params: {
				status: params.status,
				search: params.search || undefined,
				armazem: params.armazem || undefined,
				prioridade: params.prioridade || undefined,
				dataInicio: params.dataInicio || undefined,
				dataFim: params.dataFim || undefined,
				orderBy: params.orderBy || undefined,
				orderDirection: params.orderDirection || undefined,
				offset: params.offset ?? 0,
				limit: params.limit ?? 50,
			},
		});

		return {
			results: Array.isArray(res.data?.results) ? res.data.results : [],
			total: Number(res.data?.total) || 0,
		};
	} catch (err) {
		console.error("Erro ao buscar ocorrências:", err);
		throw new Error("Erro ao buscar ocorrências.");
	}
}


export async function buscarRelatorioOcorrencias(params: {
	dataInicio: string;
	dataFim: string;
}) {
	const response = await api.get(`/relatorio/ocorrencias`, { params });
	return response.data;
}


export type RelatorioSaidasOutrosBarracoesItem = {
	produto_id: number;
	sku: string | null;
	ean: string | null;
	descricao: string | null;
	qtd_base: number;
	qtd_fonte: number;
	total_mov_periodo_qtd: number;
	total_mov_periodo_ocorrencias: number;
	ultima_mov_data: string | null;
};


export type RelatorioSaidasOutrosBarracoesResponse = {
	dias: number;
	limit: number;
	armazensBase: number[];
	armazensFonte: number[] | null;
	geradoEm: string;
	itens: RelatorioSaidasOutrosBarracoesItem[];
};


export async function buscarRelatorioSaidasOutrosBarracoes(params?: {
	dias?: number;
	limit?: number;
	armazensBase?: string;
	armazensFonte?: string;
}): Promise<RelatorioSaidasOutrosBarracoesResponse> {
	const response = await api.get(`/relatorio/saidas-outros-barracoes`, {
		params,
	});
	return response.data as RelatorioSaidasOutrosBarracoesResponse;
}


export type RelatorioSaidasOutrosBarracoesOndePegarItem = {
	produto_id: number;
	sku: string | null;
	ean: string | null;
	descricao: string | null;
	armazem_id: number;
	armazem: string | null;
	localizacao_id: number;
	localizacao: string | null;
	localizacao_ean: string | null;
	quantidade: number;
	qtd_a_pegar: number | null;
	total_mov_periodo_qtd: number;
	total_mov_periodo_ocorrencias: number;
	ultima_mov_data: string | null;
};


export type RelatorioSaidasOutrosBarracoesOndePegarResponse = {
	dias: number;
	limit: number;
	produtoId: number | null;
	qtdReposicao: number | null;
	armazensBase: number[];
	armazensFonte: number[] | null;
	geradoEm: string;
	itens: RelatorioSaidasOutrosBarracoesOndePegarItem[];
};


export async function buscarRelatorioSaidasOutrosBarracoesOndePegar(params?: {
	dias?: number;
	limit?: number;
	produtoId?: number;
	qtdReposicao?: number;
	armazensBase?: string;
	armazensFonte?: string;
}): Promise<RelatorioSaidasOutrosBarracoesOndePegarResponse> {
	const response = await api.get(
		`/relatorio/saidas-outros-barracoes-onde-pegar`,
		{
			params,
		},
	);
	return response.data as RelatorioSaidasOutrosBarracoesOndePegarResponse;
}


export async function buscarOcorrenciasDaLocalizacao(localizacaoId: number) {
	const response = await api.get(
		`/ocorrencia/${localizacaoId}/ocorrencias-da-localizacao`,
	);
	return response.data;
}


export async function marcarOcorrenciaConferida(ocorrenciaId: number) {
	try {
		const response = await api.patch(
			`/ocorrencia/${ocorrenciaId}/marcar-conferido`,
		);
		return response.data;
	} catch (error) {
		console.error("Erro ao marcar ocorrência como conferida:", error);
		throw error;
	}
}

